import { CalendarArrivalWindowDialog } from '@/components/scheduler/CalendarArrivalWindowDialog';
import type { ScheduleNotificationChoice } from '@/hooks/jobs/useScheduleChangeNotification';
import {
  parseCalendarArrivalWindowConflict,
  type CalendarArrivalWindowConflict,
  type CalendarArrivalWindowChange,
} from '@/lib/scheduler/calendarArrivalWindowChange';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { ScheduleOverrideDialog } from '@/components/scheduler/ScheduleOverrideDialog';
import type { ConflictListTechnician } from '@/components/scheduler/ConflictList';
import { parseConflictError, type ScheduleConflict } from '@/lib/scheduler/scheduleConflicts';

export interface ConflictOverrideRunOptions {
  appointmentId?: string;
  title?: string;
  confirmLabel?: string;
  technicians?: ConflictListTechnician[];
  /** The user cancelled the dialog (e.g. revert an optimistic drag). */
  onDeclined?: () => void;
}

type OverrideDecision =
  | boolean
  | CalendarArrivalWindowChange
  | {
      overrideConflicts: true;
      notification?: ScheduleNotificationChoice;
    };

interface PendingOverride {
  confirmationAppointmentId?: string;
  arrivalConflict?: CalendarArrivalWindowConflict;
  conflicts: ScheduleConflict[];
  message: string;
  /** False: the server refuses this write whatever is acknowledged — no retry is offered. */
  overridable: boolean;
  title?: string;
  confirmLabel?: string;
  technicians?: ConflictListTechnician[];
  resolve: (confirmed: OverrideDecision) => void;
}

export interface ConflictOverride {
  /**
   * Run a staff write that may 409 on conflicts. `attempt(false)` first; on a
   * conflict 409 the "Schedule anyway?" dialog opens — confirm re-runs
   * `attempt(true)` (the caller sends `overrideConflicts: true`), cancel calls
   * `opts.onDeclined` and resolves `undefined`. A 409 the server marks as not
   * overridable (an overlap while the tenant disallows overlapping schedules)
   * opens the same dialog with no way to save: closing it is the cancel. Any
   * other error rethrows.
   */
  run: <T>(
    attempt: (
      overrideConflicts: boolean,
      arrivalWindowChange?: CalendarArrivalWindowChange,
      notification?: ScheduleNotificationChoice,
    ) => Promise<T>,
    opts?: ConflictOverrideRunOptions,
  ) => Promise<T | undefined>;
  /** Render once in the host component. */
  dialog: ReactNode;
  /** True between a confirmed dialog and the retry settling. */
  pending: boolean;
}

/**
 * Reactive 409 → acknowledge → retry loop for optimistic surfaces (drag on the
 * dispatch board / calendar, inline assign in the booking sheet) where there
 * is no form to preflight in.
 */
export function useConflictOverride({
  allowArrivalWindowChange = false,
}: { allowArrivalWindowChange?: boolean } = {}): ConflictOverride {
  // What the dialog shows, kept after it settles: the closing animation still
  // shows the answer the user gave. Clearing it swapped the default title and a
  // "Schedule anyway" button into a refusal while it faded out.
  const [shownOverride, setShownOverride] = useState<PendingOverride | null>(null);
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // A second `run` while a dialog is open cancels the first (its drag reverts) so
  // two overrides never race for one dialog.
  const activeRef = useRef<PendingOverride | null>(null);

  useEffect(
    () => () => {
      activeRef.current?.resolve(false);
      activeRef.current = null;
    },
    [],
  );

  const settle = useCallback((confirmed: OverrideDecision) => {
    const active = activeRef.current;
    if (!active) return;
    activeRef.current = null;
    setOpen(false);
    active.resolve(confirmed);
  }, []);

  const run = useCallback(
    async <T,>(
      attempt: (
        overrideConflicts: boolean,
        arrivalWindowChange?: CalendarArrivalWindowChange,
        notification?: ScheduleNotificationChoice,
      ) => Promise<T>,
      opts?: ConflictOverrideRunOptions,
    ): Promise<T | undefined> => {
      let acknowledged = false;
      let windowChange: CalendarArrivalWindowChange | undefined;
      let notification: ScheduleNotificationChoice | undefined;
      for (;;) {
        setRetrying(acknowledged || Boolean(windowChange));
        try {
          return await (notification
            ? attempt(acknowledged, windowChange, notification)
            : windowChange
              ? attempt(acknowledged, windowChange)
              : attempt(acknowledged));
        } catch (err) {
          const arrivalConflict = allowArrivalWindowChange
            ? parseCalendarArrivalWindowConflict(err)
            : null;
          const conflict = parseConflictError(err);
          if (arrivalConflict ? Boolean(windowChange) : !conflict || acknowledged) throw err;
          if (activeRef.current) settle(false);
          const confirmed = await new Promise<OverrideDecision>((resolve) => {
            const next: PendingOverride = {
              confirmationAppointmentId:
                allowArrivalWindowChange &&
                conflict?.conflicts.some((c) => c.type === 'ARRIVAL_WINDOW_VIOLATED')
                  ? opts?.appointmentId
                  : undefined,
              arrivalConflict: arrivalConflict ?? undefined,
              conflicts: conflict?.conflicts ?? [],
              message: conflict?.message ?? '',
              overridable: conflict?.overridable ?? true,
              title: opts?.title,
              confirmLabel: opts?.confirmLabel,
              technicians: opts?.technicians,
              resolve,
            };
            activeRef.current = next;
            setShownOverride(next);
            setOpen(true);
          });
          if (!confirmed) {
            opts?.onDeclined?.();
            return undefined;
          }
          if (typeof confirmed === 'object' && 'overrideConflicts' in confirmed) {
            acknowledged = true;
            notification = confirmed.notification;
          } else if (typeof confirmed === 'object') windowChange = confirmed;
          else acknowledged = true;
        } finally {
          setRetrying(false);
        }
      }
    },
    [settle, allowArrivalWindowChange],
  );

  // The dialog stays mounted (closed) so its exit animation can play; `pending`
  // keeps the buttons locked while the confirmed retry is in flight.
  const dialog =
    open && shownOverride?.arrivalConflict ? (
      <CalendarArrivalWindowDialog
        key={
          shownOverride.arrivalConflict.expectedUpdatedAt +
          shownOverride.arrivalConflict.proposedStartAt
        }
        conflict={shownOverride.arrivalConflict}
        onCancel={() => settle(false)}
        onConfirm={settle}
      />
    ) : (
      <ScheduleOverrideDialog
        key={open ? (shownOverride?.confirmationAppointmentId ?? 'conflict') : 'closed'}
        confirmationAppointmentId={shownOverride?.confirmationAppointmentId}
        open={open}
        conflicts={shownOverride?.conflicts ?? []}
        message={shownOverride?.message}
        blocked={shownOverride?.overridable === false}
        technicians={shownOverride?.technicians}
        title={shownOverride?.title}
        confirmLabel={shownOverride?.confirmLabel}
        onCancel={() => settle(false)}
        onConfirm={(notification) =>
          settle(notification ? { overrideConflicts: true, notification } : true)
        }
        pending={retrying}
      />
    );

  return { run, dialog, pending: retrying };
}
